import React, { useEffect, useRef, useState } from 'react';
import {
    CameraOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    DeleteOutlined,
} from '@ant-design/icons';
import {
    startCameraBarcodeScanner,
    WAREHOUSE_BARCODE_FORMATS,
} from './cameraBarcodeScanner';
import {
    clearRecentOrders,
    processScannedCode,
    readRecentOrders,
    subscribeScannerSession,
} from './scannerSession';
import { unlockScannerAudio } from './scannerAudio';
import './ScannerMobile.css';

const ScannerMobile = () => {
    const videoRef = useRef(null);
    const [cameraKey, setCameraKey] = useState(0);
    const [cameraState, setCameraState] = useState('starting');
    const [cameraError, setCameraError] = useState('');
    const [processing, setProcessing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [recentOrders, setRecentOrders] = useState(readRecentOrders);

    useEffect(() => {
        return subscribeScannerSession((event) => {
            if (event.processing != null) setProcessing(event.processing);
            if (event.lastResult) setLastResult(event.lastResult);
            if (event.recentOrders) setRecentOrders(event.recentOrders);
        });
    }, []);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        let cancelled = false;
        let stopScanner = () => {};
        setCameraState('starting');
        setCameraError('');

        void startCameraBarcodeScanner({
            video,
            formats: WAREHOUSE_BARCODE_FORMATS,
            onDetect: (text) => {
                void processScannedCode(text);
            },
            onReady: () => {
                if (!cancelled) setCameraState('ready');
            },
            onError: (error) => {
                if (cancelled) return;
                setCameraState('error');
                setCameraError(error.message);
            },
        }).then((stop) => {
            if (cancelled) {
                stop();
                return;
            }
            stopScanner = stop;
        });

        return () => {
            cancelled = true;
            stopScanner();
        };
    }, [cameraKey]);

    return (
        <section className="mobile-scanner" onPointerDown={unlockScannerAudio}>
            <div className="mobile-scanner__camera">
                <video ref={videoRef} autoPlay muted playsInline />
                <div className="mobile-scanner__shade" aria-hidden="true">
                    <div className="mobile-scanner__target">
                        <span />
                    </div>
                </div>
                <header className="mobile-scanner__camera-header">
                    <div>
                        <strong>Сканер</strong>
                        <span>
                            {processing
                                ? 'Проверяем код…'
                                : cameraState === 'ready'
                                  ? 'Наведите камеру или сканер на код'
                                  : cameraState === 'error'
                                    ? 'Аппаратный сканер готов'
                                    : 'Запускаем камеру…'}
                        </span>
                    </div>
                    <CameraOutlined />
                </header>

                {cameraState === 'error' && (
                    <div className="mobile-scanner__camera-error">
                        <p>{cameraError}</p>
                        <p className="mobile-scanner__camera-hint">
                            Вкладка слушает аппаратный сканер. Оставьте её активной и сканируйте
                            код.
                        </p>
                        <button type="button" onClick={() => setCameraKey((key) => key + 1)}>
                            Включить камеру
                        </button>
                    </div>
                )}
            </div>

            <div className="mobile-scanner__results">
                {lastResult ? (
                    <article
                        className={`mobile-scanner__result mobile-scanner__result--${lastResult.type}`}
                    >
                        {lastResult.type === 'error' ? (
                            <CloseCircleFilled />
                        ) : (
                            <CheckCircleFilled />
                        )}
                        <div>
                            <strong>{lastResult.message}</strong>
                            <span>{lastResult.scannedText}</span>
                            {lastResult.orderId && <b>{lastResult.orderId}</b>}
                        </div>
                    </article>
                ) : (
                    <div className="mobile-scanner__empty-result">
                        Результат последнего сканирования появится здесь
                    </div>
                )}

                <div className="mobile-scanner__list-header">
                    <h2>Отсканированные заказы</h2>
                    <div className="mobile-scanner__list-actions">
                        <span>{recentOrders.length}</span>
                        <button
                            type="button"
                            onClick={clearRecentOrders}
                            disabled={!recentOrders.length}
                        >
                            <DeleteOutlined />
                            Очистить
                        </button>
                    </div>
                </div>

                <div className="mobile-scanner__list">
                    {recentOrders.length ? (
                        recentOrders.map((item, index) => (
                            <div key={item.orderId} className="mobile-scanner__list-item">
                                <span>{index + 1}</span>
                                <strong>{item.orderId}</strong>
                                <time>
                                    {new Date(item.scannedAt).toLocaleTimeString('ru-RU', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </time>
                            </div>
                        ))
                    ) : (
                        <p className="mobile-scanner__list-empty">Список пока пуст</p>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ScannerMobile;
